import React, { useEffect, useState, useCallback } from 'react';

import { Link } from 'react-router-dom';

import { Divider, Modal } from '@material-ui/core';
import { FiEdit3, FiTrash2 } from 'react-icons/fi';

import api from '../../services/api';

import { useAuth } from '../../hooks/auth';
import { useToast } from '../../hooks/toast';

import Toggle from '../../newComponents/Toggle';
import Button from '../../newComponents/Button';

import * as S from './styles';

export interface CategoriaFiscal {
  id: number;
  descricao: string;
  ncm: string;
  cfop_interno: string;
  ativo: boolean;
  grupo?: { id: number; descricao: string } | null;
}

const CategoriasFiscais: React.FC = () => {
  const [categorias, setCategorias] = useState<CategoriaFiscal[]>([]);
  const [deleteId, setDeleteId] = useState<number | null>(null);

  const { user } = useAuth();
  const { addToast } = useToast();

  const loadCategorias = useCallback(async (): Promise<void> => {
    try {
      const response = await api.get(
        `companies/${user.empresa_id}/categorias-fiscais`,
      );
      setCategorias(response.data);
    } catch (err) {
      addToast({ type: 'error', title: 'Erro ao carregar categorias fiscais' });
    }
  }, [user.empresa_id, addToast]);

  useEffect(() => {
    loadCategorias();
  }, [loadCategorias]);

  const handleChangeAtivo = useCallback(
    async (categoriaId: number, ativo: boolean) => {
      try {
        const response = await api.patch(
          `companies/${user.empresa_id}/categorias-fiscais/${categoriaId}`,
          { ativo },
        );

        addToast({
          type: 'success',
          title: response.data.ativo
            ? 'Categoria ativada'
            : 'Categoria desativada',
        });

        setCategorias((current) =>
          current.map((categoria) =>
            categoria.id === categoriaId
              ? { ...categoria, ativo: response.data.ativo }
              : categoria,
          ),
        );
      } catch (err) {
        addToast({ type: 'error', title: 'Erro ao atualizar status' });
      }
    },
    [user.empresa_id, addToast],
  );

  const handleDelete = useCallback(async () => {
    if (!deleteId) return;

    try {
      await api.delete(
        `companies/${user.empresa_id}/categorias-fiscais/${deleteId}`,
      );

      addToast({ type: 'success', title: 'Categoria excluída' });
      setCategorias((current) =>
        current.filter((categoria) => categoria.id !== deleteId),
      );
    } catch (err) {
      addToast({ type: 'error', title: 'Erro ao excluir categoria' });
    } finally {
      setDeleteId(null);
    }
  }, [deleteId, user.empresa_id, addToast]);

  return (
    <S.Container>
      <div className="header">
        <h2>Categorias Fiscais</h2>
        <p>
          Cada produto herda a tributação de uma categoria fiscal (NCM, CFOP,
          CEST, origem e grupo de regras tributárias).
        </p>
      </div>

      <Divider />

      <div className="create_user">
        <Link to="/categorias-fiscais/new">Adicionar Categoria</Link>
      </div>

      <S.ContainerList>
        <S.CustomTable>
          <S.TableHeader>
            <span>Descrição</span>
            <span>NCM</span>
            <span>CFOP</span>
            <span>Grupo</span>
            <span>Editar</span>
            <span>Excluir</span>
            <span>Status</span>
          </S.TableHeader>
          <S.TableBody>
            {categorias.map((categoria) => (
              <S.TableRow key={categoria.id}>
                <S.TableCellRow>
                  <p>{categoria.descricao}</p>
                </S.TableCellRow>
                <S.TableCellRow>
                  <p>{categoria.ncm}</p>
                </S.TableCellRow>
                <S.TableCellRow>
                  <p>{categoria.cfop_interno}</p>
                </S.TableCellRow>
                <S.TableCellRow>
                  <p>{categoria.grupo?.descricao || '-'}</p>
                </S.TableCellRow>
                <S.TableCellRow>
                  <Link to={`/categorias-fiscais/edit/${categoria.id}`}>
                    <FiEdit3 size={20} />
                  </Link>
                </S.TableCellRow>
                <S.TableCellRow>
                  <button
                    type="button"
                    onClick={() => setDeleteId(categoria.id)}
                  >
                    <FiTrash2 size={20} />
                  </button>
                </S.TableCellRow>
                <S.TableCellRow>
                  <Toggle
                    checked={categoria.ativo}
                    firstText="Inativo"
                    secondText="Ativo"
                    onToggle={(value) => handleChangeAtivo(categoria.id, value)}
                  />
                </S.TableCellRow>
              </S.TableRow>
            ))}
          </S.TableBody>
        </S.CustomTable>
      </S.ContainerList>

      <Modal open={!!deleteId} onClose={() => setDeleteId(null)}>
        <S.ModalContent>
          <h1>Excluir categoria fiscal?</h1>
          <p>Esta ação não pode ser desfeita.</p>
          <div className="modal-actions">
            <Button
              variant="outlined"
              full
              type="button"
              onClick={() => setDeleteId(null)}
            >
              Cancelar
            </Button>
            <Button full type="button" onClick={handleDelete}>
              Excluir
            </Button>
          </div>
        </S.ModalContent>
      </Modal>
    </S.Container>
  );
};

export default CategoriasFiscais;
